import React, { useState } from 'react';
import { FiMessageCircle } from 'react-icons/fi';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { addressOf, billingDocumentLabel, formatDate, money, outstandingOf, paymentTotal } from '../../utils/billingUtils';
import WhatsAppComposer, { billingContacts } from './WhatsAppComposer';

function today() { return new Date().toISOString().slice(0, 10); }

const NAVY = [11, 31, 51];
const GOLD = [200, 169, 107];
const IVORY = [247, 243, 232];
const SLATE = [85, 105, 125];
const LIGHT = [246, 248, 250];
const BORDER = [215, 222, 229];

function normalise(value) {
  return String(value || '').trim().toLowerCase();
}

function isDcsCompany(company = {}, proforma = {}) {
  const value = normalise(company.legalName || proforma.companyName);
  return value.includes('d.c. shukla') || value.includes('dc shukla');
}


function companyKind(company = {}, proforma = {}) {
  const value = normalise([company.name, company.legalName, proforma.companyName].filter(Boolean).join(' '));
  if (value.includes('global advisors')) return 'taxempt-global';
  if (value.includes('taxempt')) return 'taxempt';
  if (value.includes('d.c. shukla') || value.includes('dc shukla')) return 'dcs';
  return 'other';
}

function defaultBranding(kind) {
  if (kind === 'dcs') return {
    logoPath: '/brand/organisation-logo.png',
    companyName: 'D.C. Shukla & Co.',
    tagline: 'Chartered Accountants',
    invoiceTitle: 'Proforma Invoice',
  };
  if (kind === 'taxempt') return {
    logoPath: '/brand/taxempt-global-logo.jpg',
    companyName: 'Taxempt',
    tagline: 'Your Global Business Advisors',
    invoiceTitle: 'Invoice',
  };
  if (kind === 'taxempt-global') return {
    logoPath: '/brand/taxempt-global-logo.jpg',
    companyName: 'Taxempt Global Advisors Private Limited',
    tagline: 'Your Global Business Advisors',
    invoiceTitle: 'Invoice',
  };
  return { logoPath: '', companyName: '', tagline: '', invoiceTitle: 'Proforma Invoice' };
}

function cleanCompanyName(value, kind) {
  const name = String(value || '').trim();
  if (kind === 'dcs') return name.replace(/\s*,?\s*chartered\s+accountants\s*$/i, '').trim() || 'D.C. Shukla & Co.';
  return name;
}

async function loadFirstImage(paths = [], options = {}) {
  for (const path of paths.filter(Boolean)) {
    if (path === 'none') return null;
    // eslint-disable-next-line no-await-in-loop
    const image = await loadPdfImage(path, options);
    if (image) return image;
  }
  return null;
}

function isGstApplicable(company = {}, proforma = {}) {
  return Boolean(company.gstApplicable || company.gstin || Number(proforma.gstAmount || 0) > 0);
}

function sameGstState(company = {}, client = {}) {
  const sellerCode = String(company.gstin || '').slice(0, 2);
  const buyerCode = String(client.gstin || '').slice(0, 2);
  if (sellerCode && buyerCode && /^\d{2}$/.test(sellerCode) && /^\d{2}$/.test(buyerCode)) return sellerCode === buyerCode;
  return normalise(client.state).includes('uttar pradesh');
}

function wordsBelowHundred(value) {
  const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine', 'Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
  const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
  if (value < 20) return ones[value];
  return `${tens[Math.floor(value / 10)]}${value % 10 ? ` ${ones[value % 10]}` : ''}`;
}

function wordsBelowThousand(value) {
  if (!value) return '';
  const hundreds = Math.floor(value / 100);
  const remainder = value % 100;
  return `${hundreds ? `${wordsBelowHundred(hundreds)} Hundred` : ''}${hundreds && remainder ? ' ' : ''}${remainder ? wordsBelowHundred(remainder) : ''}`;
}

function integerToIndianWords(value) {
  const amount = Math.floor(Math.abs(Number(value || 0)));
  if (amount === 0) return 'Zero';
  const parts = [];
  const crore = Math.floor(amount / 10000000);
  const lakh = Math.floor((amount % 10000000) / 100000);
  const thousand = Math.floor((amount % 100000) / 1000);
  const remainder = amount % 1000;
  if (crore) parts.push(`${integerToIndianWords(crore)} Crore`);
  if (lakh) parts.push(`${wordsBelowThousand(lakh)} Lakh`);
  if (thousand) parts.push(`${wordsBelowThousand(thousand)} Thousand`);
  if (remainder) parts.push(wordsBelowThousand(remainder));
  return parts.join(' ');
}

function amountInWords(value) {
  const amount = Math.round(Number(value || 0) * 100) / 100;
  const rupees = Math.floor(amount);
  const paise = Math.round((amount - rupees) * 100);
  return `Rupees ${integerToIndianWords(rupees)}${paise ? ` and ${integerToIndianWords(paise)} Paise` : ''} Only`;
}

function loadImage(path) {
  return new Promise((resolve) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => resolve(null);
    image.src = `${process.env.PUBLIC_URL}${path}`;
  });
}

// jsPDF can embed transparent PNGs as large uncompressed image streams.
// The invoice uses a white background, so flattening local artwork to a modest
// JPEG before adding it to the PDF keeps a one-page invoice small without a
// visible loss at the displayed dimensions.
function compressImageForPdf(image, { maxWidth = 700, maxHeight = 300, quality = 0.78 } = {}) {
  if (!image) return null;
  const naturalWidth = Number(image.naturalWidth || image.width || 1);
  const naturalHeight = Number(image.naturalHeight || image.height || 1);
  const scale = Math.min(1, maxWidth / naturalWidth, maxHeight / naturalHeight);
  const width = Math.max(1, Math.round(naturalWidth * scale));
  const height = Math.max(1, Math.round(naturalHeight * scale));
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext('2d');
  if (!context) return image;
  context.fillStyle = '#FFFFFF';
  context.fillRect(0, 0, width, height);
  context.drawImage(image, 0, 0, width, height);
  return canvas.toDataURL('image/jpeg', quality);
}

async function loadPdfImage(path, options = {}) {
  const image = await loadImage(path);
  return compressImageForPdf(image, options);
}

function safeText(value, fallback = '—') {
  const text = String(value || '').trim();
  return text || fallback;
}

function addFooter(doc, symbolImage, wordmarkImage, footerText = 'Computer-generated document') {
  const pages = doc.getNumberOfPages();
  for (let page = 1; page <= pages; page += 1) {
    doc.setPage(page);
    const height = doc.internal.pageSize.getHeight();
    const width = doc.internal.pageSize.getWidth();
    doc.setDrawColor(...BORDER);
    doc.line(14, height - 17, width - 14, height - 17);
    if (symbolImage) doc.addImage(symbolImage, 'JPEG', 14, height - 14.5, 6.5, 6.5);
    doc.setTextColor(...SLATE);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7.5);
    doc.text('Generated by', symbolImage ? 22 : 14, height - 10);
    if (wordmarkImage) doc.addImage(wordmarkImage, 'JPEG', symbolImage ? 39 : 31, height - 14, 18, 7);
    else doc.text('Pravah', symbolImage ? 39 : 31, height - 10);
    doc.text(safeText(footerText, 'Computer-generated document'), width - 14, height - 10, { align: 'right' });
  }
}

function ensureSpace(doc, y, needed = 30) {
  const maxY = doc.internal.pageSize.getHeight() - 28;
  if (y + needed <= maxY) return y;
  doc.addPage();
  return 20;
}


function pdfFilename(proforma = {}) {
  const number = safeText(proforma.proformaNumber, 'Proforma').replace(/[\\/:*?"<>|]/g, '-');
  return `${number}.pdf`;
}

function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 1500);
}

function normaliseWhatsAppNumber(value) {
  let digits = String(value || '').replace(/\D/g, '');
  if (digits.startsWith('00')) digits = digits.slice(2);
  if (digits.length === 11 && digits.startsWith('0')) digits = digits.slice(1);
  if (digits.length === 10) digits = `91${digits}`;
  return /^\d{11,15}$/.test(digits) ? digits : '';
}

function whatsappMessage(proforma = {}) {
  const companyName = safeText(proforma.companyName, 'Our office');
  return [
    'Dear Sir/Madam,',
    '',
    `Please find attached ${billingDocumentLabel(proforma)} No. ${safeText(proforma.proformaNumber)} dated ${formatDate(proforma.proformaDate)} for ₹${money(proforma.grandTotal)}.`,
    '',
    'Regards,',
    companyName,
    '',
    '',
    '*System-generated communication via Pravah.*',
  ].join('\n');
}

export default function ProformaDetailModal({ proforma, client = null, canManage, onClose, onEdit, onMarkSent, onCancel, onAddFollowUp, onAddPayment }) {
  const [followUp, setFollowUp] = useState({ date: today(), remarks: '', outcome: 'Reminder sent', nextFollowUpDate: '' });
  const [payment, setPayment] = useState({ date: today(), amount: '', mode: 'Bank Transfer', reference: '', remarks: '' });
  const [tab, setTab] = useState('details');
  const [shareNotice, setShareNotice] = useState('');
  const [shareError, setShareError] = useState('');
  const [sharing, setSharing] = useState(false);
  const [whatsappMode, setWhatsappMode] = useState('');
  if (!proforma) return null;

  const createPdfDocument = async () => {
    const doc = new jsPDF({ unit: 'mm', format: 'a4' });
    const company = proforma.companySnapshot || {};
    const client = proforma.clientSnapshot || {};
    const kind = companyKind(company, proforma);
    const defaults = defaultBranding(kind);
    const dcsLayout = kind === 'dcs';
    const gstApplicable = isGstApplicable(company, proforma);
    const intrastate = sameGstState(company, client);
    const configuredLogoPath = String(company.logoPath || '').trim();
    const [organisationLogo, symbolImage, wordmarkImage, signatureImage, paymentQrImage] = await Promise.all([
      loadFirstImage([configuredLogoPath, defaults.logoPath], { maxWidth: 600, maxHeight: 300, quality: 0.82 }),
      loadPdfImage('/brand/pravah-symbol.png', { maxWidth: 180, maxHeight: 180, quality: 0.76 }),
      loadPdfImage('/brand/pravah-wordmark.png', { maxWidth: 420, maxHeight: 160, quality: 0.76 }),
      loadPdfImage('/brand/authorised-signature.png', { maxWidth: 450, maxHeight: 150, quality: 0.74 }),
      kind === 'taxempt'
        ? loadPdfImage('/brand/taxempt-upi-qr.png', { maxWidth: 650, maxHeight: 650, quality: 0.9 })
        : kind === 'taxempt-global'
          ? loadPdfImage('/brand/taxempt-global-upi-qr.png', { maxWidth: 650, maxHeight: 650, quality: 0.9 })
          : Promise.resolve(null),
    ]);

    const pageWidth = doc.internal.pageSize.getWidth();
    const left = 14;
    const right = pageWidth - 14;

    doc.setFillColor(...NAVY);
    doc.rect(0, 0, pageWidth, 7, 'F');
    doc.setFillColor(...GOLD);
    doc.rect(0, 7, pageWidth, 1.1, 'F');

    const rawName = company.name || company.legalName || proforma.companyName || defaults.companyName;
    const companyName = cleanCompanyName(rawName, kind) || defaults.companyName || 'Billing Entity';
    const isTaxemptBrand = kind === 'taxempt' || kind === 'taxempt-global';
    const tagline = isTaxemptBrand
      ? 'Your Global Business Advisors'
      : String(company.tagline || defaults.tagline || '').trim();
    const invoiceTitle = String(company.invoiceTitle || defaults.invoiceTitle || 'Proforma Invoice').trim();

    let companyTextX = left;
    let identityY = 16.5;
    let identityWidth = 105;

    if (organisationLogo) {
      if (kind === 'taxempt' || kind === 'taxempt-global') {
        // Both Taxempt entities use the Taxempt Global logo and common header style.
        const logoWidth = 20;
        const logoHeight = 16.1;
        doc.addImage(organisationLogo, 'JPEG', left, 12, logoWidth, logoHeight);
        companyTextX = left + logoWidth + 4;
        identityWidth = 90;
      } else {
        const logoWidth = 14;
        const logoHeight = 14;
        doc.addImage(organisationLogo, 'JPEG', left, 13, logoWidth, logoHeight);
        companyTextX = left + logoWidth + 4;
        identityWidth = 94;
      }
    }

    doc.setTextColor(...NAVY);
    const taxemptHeader = kind === 'taxempt' || kind === 'taxempt-global';
    const companyFont = taxemptHeader ? 'times' : 'helvetica';
    doc.setFont(companyFont, 'bold');
    doc.setFontSize(kind === 'dcs' ? 16 : (taxemptHeader ? 14 : 15));
    const companyLines = doc.splitTextToSize(companyName, identityWidth);
    doc.text(companyLines, companyTextX, identityY);
    identityY += Math.max(1, companyLines.length) * (kind === 'dcs' ? 5.6 : 5.2);
    if (tagline) {
      doc.setFont(taxemptHeader ? 'times' : 'helvetica', taxemptHeader ? 'italic' : 'bold');
      doc.setFontSize(kind === 'dcs' ? 14 : 11.5);
      doc.text(tagline, companyTextX, identityY);
      identityY += kind === 'dcs' ? 5.4 : 4.8;
    }

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8.5);
    const addressText = String(company.address || '').trim();
    const addressLines = addressText ? doc.splitTextToSize(addressText, identityWidth) : [];
    const addressY = identityY + 1;
    if (addressLines.length) doc.text(addressLines, companyTextX, addressY);
    let sellerY = addressY + Math.max(0, addressLines.length - 1) * 4;
    if (company.pan) { sellerY += 5; doc.text(`PAN: ${company.pan}`, companyTextX, sellerY); }
    if (company.gstin) { sellerY += 4.5; doc.text(`GSTIN: ${company.gstin}`, companyTextX, sellerY); }
    if (company.email) { sellerY += 4.5; doc.text(`Email: ${company.email}`, companyTextX, sellerY); }

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(18);
    doc.text(invoiceTitle.toUpperCase(), right, 17, { align: 'right' });
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.text(`No.: ${safeText(proforma.proformaNumber)}`, right, 24, { align: 'right' });
    doc.text(`Date: ${formatDate(proforma.proformaDate)}`, right, 29, { align: 'right' });
    if (proforma.dueDate) doc.text(`Due: ${formatDate(proforma.dueDate)}`, right, 34, { align: 'right' });

    const headerBottom = Math.max(42, sellerY + 6);
    doc.setDrawColor(...BORDER);
    doc.line(left, headerBottom, right, headerBottom);

    const billTop = headerBottom + 6;
    doc.setFillColor(...LIGHT);
    doc.roundedRect(left, billTop, pageWidth - 28, 32, 2, 2, 'F');
    doc.setTextColor(...SLATE);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8);
    doc.text('BILL TO', left + 4, billTop + 6);
    doc.setTextColor(...NAVY);
    doc.setFontSize(11);
    doc.text(safeText(proforma.clientName), left + 4, billTop + 12);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8.5);
    const clientAddress = doc.splitTextToSize(addressOf(client), 105);
    if (clientAddress.length) doc.text(clientAddress, left + 4, billTop + 17);
    let clientMetaY = billTop + 17 + Math.max(0, clientAddress.length - 1) * 4;
    if (client.gstin) { clientMetaY += 5; doc.text(`GSTIN: ${client.gstin}`, left + 4, clientMetaY); }
    if (client.pan) doc.text(`PAN: ${client.pan}`, right - 4, billTop + 12, { align: 'right' });
    if (client.state) doc.text(`Place of Supply: ${client.state}`, right - 4, billTop + 18, { align: 'right' });

    const lines = (proforma.lineItems || []).map((line, index) => {
      const hsn = line.hsnSac || (dcsLayout && gstApplicable ? '998224' : '');
      return dcsLayout && gstApplicable
        ? [index + 1, line.description || '', hsn, money(line.amount)]
        : [index + 1, line.description || '', money(line.amount)];
    });

    autoTable(doc, {
      startY: billTop + 39,
      margin: { left, right: 14, bottom: 27 },
      head: [dcsLayout && gstApplicable ? ['#', 'Professional Services / Activity', 'HSN/SAC', 'Amount (INR)'] : ['#', 'Professional Services / Activity', 'Amount (INR)']],
      body: lines,
      theme: 'grid',
      headStyles: { fillColor: NAVY, textColor: IVORY, fontStyle: 'bold', fontSize: 8.5, cellPadding: 3 },
      bodyStyles: { textColor: NAVY, fontSize: 8.5, cellPadding: 3.2, lineColor: BORDER, lineWidth: 0.15 },
      alternateRowStyles: { fillColor: [250, 251, 252] },
      columnStyles: dcsLayout && gstApplicable
        ? { 0: { cellWidth: 10, halign: 'center' }, 2: { cellWidth: 25, halign: 'center' }, 3: { cellWidth: 34, halign: 'right' } }
        : { 0: { cellWidth: 10, halign: 'center' }, 2: { cellWidth: 38, halign: 'right' } },
      didDrawPage: () => {
        doc.setFillColor(...NAVY);
        doc.rect(0, 0, pageWidth, 4, 'F');
      },
    });

    let y = doc.lastAutoTable.finalY + 5;
    y = ensureSpace(doc, y, gstApplicable ? 55 : 42);
    const summaryWidth = 78;
    const summaryX = right - summaryWidth;
    const taxable = Number(proforma.taxableAmount || 0);
    const gst = Number(proforma.gstAmount || 0);
    const total = Number(proforma.grandTotal || 0);
    const summaryRows = [['Taxable Amount', money(taxable)]];
    if (gstApplicable && gst > 0) {
      summaryRows.push([`GST @ ${Number(proforma.gstRate || 0)}%`, money(gst)]);
    }
    summaryRows.push(['Grand Total', money(total)]);

    autoTable(doc, {
      startY: y,
      margin: { left: summaryX, right: 14, bottom: 27 },
      body: summaryRows,
      theme: 'grid',
      styles: { fontSize: 8.7, cellPadding: 2.7, lineColor: BORDER, lineWidth: 0.15, textColor: NAVY },
      columnStyles: { 0: { cellWidth: 48, fontStyle: 'bold' }, 1: { cellWidth: 30, halign: 'right' } },
      didParseCell: (data) => {
        if (data.row.index === summaryRows.length - 1) {
          data.cell.styles.fillColor = NAVY;
          data.cell.styles.textColor = IVORY;
          data.cell.styles.fontStyle = 'bold';
        }
      },
    });
    y = doc.lastAutoTable.finalY + 7;

    y = ensureSpace(doc, y, 35);
    doc.setFillColor(...LIGHT);
    const words = amountInWords(total);
    const wordLines = doc.splitTextToSize(words, pageWidth - 36);
    const wordsHeight = Math.max(15, 8 + wordLines.length * 4);
    doc.roundedRect(left, y, pageWidth - 28, wordsHeight, 1.5, 1.5, 'F');
    doc.setTextColor(...SLATE);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(7.5);
    doc.text('AMOUNT IN WORDS', left + 4, y + 5);
    doc.setTextColor(...NAVY);
    doc.setFontSize(9);
    doc.text(wordLines, left + 4, y + 10);
    y += wordsHeight + 6;

    if (gstApplicable && gst > 0 && dcsLayout) {
      y = ensureSpace(doc, y, 24);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(...NAVY);
      doc.setFontSize(8.5);
      doc.text('GST SUMMARY', left, y);
      autoTable(doc, {
        startY: y + 3,
        margin: { left, right: 14, bottom: 27 },
        head: [['HSN/SAC', 'Taxable Value', 'GST', 'Total Tax']],
        body: [['998224', money(taxable), money(gst), money(gst)]],
        theme: 'grid',
        headStyles: { fillColor: [228, 234, 239], textColor: NAVY, fontStyle: 'bold', fontSize: 7.5 },
        bodyStyles: { fontSize: 8, textColor: NAVY, lineColor: BORDER, lineWidth: 0.15 },
        columnStyles: { 1: { halign: 'right' }, 2: { halign: 'right' }, 3: { halign: 'right' } },
      });
      y = doc.lastAutoTable.finalY + 7;
    }

    y = ensureSpace(doc, y, 52);
    const blockGap = 6;
    const blockWidth = (pageWidth - 28 - blockGap) / 2;
    const leftBlockX = left;
    const rightBlockX = left + blockWidth + blockGap;
    const detailsHeight = 46;
    doc.setDrawColor(...BORDER);
    doc.roundedRect(leftBlockX, y, blockWidth, detailsHeight, 1.5, 1.5, 'S');
    doc.roundedRect(rightBlockX, y, blockWidth, detailsHeight, 1.5, 1.5, 'S');

    doc.setTextColor(...NAVY);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8.5);
    doc.text('BANK DETAILS', leftBlockX + 4, y + 6);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    const bankText = safeText(company.bankDetails, 'Bank details not entered');
    const showPaymentQr = (kind === 'taxempt' || kind === 'taxempt-global') && Boolean(paymentQrImage);
    const bankTextWidth = showPaymentQr ? blockWidth - 40 : blockWidth - 8;
    doc.text(doc.splitTextToSize(bankText, bankTextWidth), leftBlockX + 4, y + 12);

    // Taxempt and Taxempt Global use their own UPI payment QR. D.C. Shukla & Co.
    // intentionally continues without a QR.
    if (showPaymentQr) {
      const qrSize = 27;
      const qrX = leftBlockX + blockWidth - qrSize - 4;
      const qrY = y + 9;
      doc.addImage(paymentQrImage, 'JPEG', qrX, qrY, qrSize, qrSize);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(7);
      doc.setTextColor(...SLATE);
      doc.text('SCAN TO PAY', qrX + (qrSize / 2), qrY + qrSize + 4, { align: 'center' });
      doc.setTextColor(...NAVY);
    }

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8.5);
    doc.text('PAYMENT & AUTHORISATION', rightBlockX + 4, y + 6);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    const terms = safeText(proforma.paymentTerms || company.paymentTerms, 'Payment due on receipt');
    doc.text(doc.splitTextToSize(`Payment Terms: ${terms}`, blockWidth - 8), rightBlockX + 4, y + 12);
    doc.setFont('helvetica', 'bold');
    doc.text(`For ${safeText(company.name || company.legalName || proforma.companyName)}`, rightBlockX + blockWidth - 4, y + 25, { align: 'right', maxWidth: blockWidth - 8 });

    // Personal Pravah installation: common authorised-signatory image for all billing entities.
    // The PNG is stored locally in public/brand and is not uploaded to Firebase Storage.
    if (signatureImage) {
      const signatureWidth = 30;
      const signatureHeight = 9;
      doc.addImage(
        signatureImage,
        'JPEG',
        rightBlockX + blockWidth - signatureWidth - 4,
        y + 27,
        signatureWidth,
        signatureHeight,
      );
    }

    doc.setFont('helvetica', 'normal');
    doc.text(
      safeText(company.signatoryLabel, 'Authorised Signatory'),
      rightBlockX + blockWidth - 4,
      y + 42,
      { align: 'right' },
    );

    y += detailsHeight + 5;
    if (proforma.notes) {
      y = ensureSpace(doc, y, 15);
      doc.setTextColor(...SLATE);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(7.5);
      doc.text('NOTES', left, y);
      doc.setTextColor(...NAVY);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8);
      doc.text(doc.splitTextToSize(proforma.notes, pageWidth - 28), left, y + 5);
    }

    addFooter(doc, symbolImage, wordmarkImage, company.footerText);
    return doc;
  };

  const downloadPdf = async () => {
    const doc = await createPdfDocument();
    doc.save(pdfFilename(proforma));
  };

  const contacts = billingContacts(client || {}, proforma.clientSnapshot || {});
  const openBillWhatsApp = () => {
    setShareNotice('');
    setShareError('');
    if (!contacts.length) {
      setShareError('No valid saved mobile number is available for this client. Please update Client Master.');
      return;
    }
    setWhatsappMode('bill');
  };

  const openFollowUpWhatsApp = () => {
    setShareNotice('');
    setShareError('');
    if (!contacts.length) {
      setShareError('No valid saved mobile number is available for this client. Please update Client Master.');
      return;
    }
    setWhatsappMode('followup');
  };

  const confirmWhatsApp = async ({ phone, message }) => {
    const likelyMobile = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent || '');
    const pendingWindow = likelyMobile ? null : window.open('about:blank', '_blank');
    setSharing(true);
    try {
      if (whatsappMode === 'bill') {
        const doc = await createPdfDocument();
        const blob = doc.output('blob');
        downloadBlob(blob, pdfFilename(proforma));
      }
      const url = likelyMobile
        ? `https://wa.me/${phone}?text=${encodeURIComponent(message)}`
        : `https://web.whatsapp.com/send?phone=${phone}&text=${encodeURIComponent(message)}`;
      if (pendingWindow) pendingWindow.location.href = url;
      else window.open(url, '_blank', 'noopener,noreferrer');
      setWhatsappMode('');
      setShareNotice(whatsappMode === 'bill'
        ? 'PDF downloaded and WhatsApp opened. Attach the PDF from Downloads, send the message, then use Mark Sent to record dispatch.'
        : 'WhatsApp opened with the follow-up message ready. After sending, record the follow-up outcome below.');
    } catch (error) {
      if (pendingWindow && !pendingWindow.closed) pendingWindow.close();
      setShareError(`Could not prepare WhatsApp: ${error?.message || 'Unknown error'}`);
    } finally {
      setSharing(false);
    }
  };

  const whatsappInitialMessage = whatsappMode === 'followup'
    ? [
        'Dear Sir/Madam,',
        '',
        `This is a gentle reminder regarding ${billingDocumentLabel(proforma)} No. ${safeText(proforma.proformaNumber)}. The balance outstanding is ₹${money(outstandingOf(proforma))}.`,
        '',
        'Kindly arrange payment at your convenience.',
        '',
        'Regards,',
        safeText(proforma.companyName, 'Our office'),
        '',
        '',
        '*System-generated communication via Pravah.*',
      ].join('\n')
    : whatsappMessage(proforma);

  const submitFollowUp = async (event) => {
    event.preventDefault();
    if (!followUp.remarks.trim()) return;
    await onAddFollowUp(proforma, followUp);
    setFollowUp({ date: today(), remarks: '', outcome: 'Reminder sent', nextFollowUpDate: '' });
  };
  const submitPayment = async (event) => {
    event.preventDefault();
    if (Number(payment.amount) <= 0 || Number(payment.amount) > outstandingOf(proforma) + 0.01) return;
    await onAddPayment(proforma, payment);
    setPayment({ date: today(), amount: '', mode: 'Bank Transfer', reference: '', remarks: '' });
  };

  return <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-3"><div className="bg-white rounded-lg shadow-xl w-full max-w-5xl max-h-[96vh] overflow-y-auto">
    <div className="sticky top-0 bg-white border-b px-5 py-4 z-10"><div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between"><div><h2 className="text-xl font-bold">{proforma.proformaNumber}</h2><p className="text-sm text-gray-500">{proforma.clientName}</p></div><div className="flex flex-wrap items-center gap-2">{canManage && proforma.status === 'Draft' && !(proforma.payments || []).length && <button onClick={() => onEdit(proforma)} className="px-3 py-2 border border-blue-600 text-blue-700 rounded text-sm">Edit</button>}<button onClick={downloadPdf} className="px-3 py-2 border rounded text-sm">Download PDF</button><button type="button" onClick={openBillWhatsApp} disabled={sharing} className="inline-flex items-center gap-2 rounded bg-green-600 px-3 py-2 text-sm font-medium text-white hover:bg-green-700 disabled:cursor-not-allowed disabled:opacity-60"><FiMessageCircle /> {sharing ? 'Preparing…' : 'WhatsApp'}</button>{canManage && proforma.status === 'Draft' && <button onClick={() => onMarkSent(proforma)} className="px-3 py-2 bg-blue-600 text-white rounded text-sm">Mark Sent</button>}{canManage && proforma.status !== 'Cancelled' && !(proforma.payments || []).length && <button onClick={() => onCancel(proforma)} className="px-3 py-2 border border-red-500 text-red-700 rounded text-sm">Cancel</button>}<button onClick={onClose} className="text-2xl px-2">×</button></div></div>{shareError && <div className="mt-3 rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{shareError}</div>}{shareNotice && <div className="mt-3 rounded border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-800">{shareNotice}</div>}</div>
    <div className="px-5 pt-4 flex gap-2 border-b"><button onClick={() => setTab('details')} className={`px-3 py-2 ${tab === 'details' ? 'border-b-2 border-blue-600 font-semibold' : ''}`}>Details</button><button onClick={() => setTab('followup')} className={`px-3 py-2 ${tab === 'followup' ? 'border-b-2 border-blue-600 font-semibold' : ''}`}>Follow-ups</button><button onClick={() => setTab('payments')} className={`px-3 py-2 ${tab === 'payments' ? 'border-b-2 border-blue-600 font-semibold' : ''}`}>Payments</button></div>
    <div className="p-5">
      {tab === 'details' && <div>
        <div className="grid md:grid-cols-4 gap-3 mb-5"><div className="bg-gray-50 p-3 rounded"><div className="text-xs text-gray-500">Status</div><div className="font-bold">{proforma.status}</div>{proforma.sentAt && <div className="text-xs text-gray-500 mt-1">Sent {formatDate(proforma.sentAt)}{proforma.sentMode ? ` · ${proforma.sentMode}` : ''}</div>}</div><div className="bg-gray-50 p-3 rounded"><div className="text-xs text-gray-500">Payment</div><div className="font-bold">{proforma.paymentStatus || 'Unpaid'}</div></div><div className="bg-gray-50 p-3 rounded"><div className="text-xs text-gray-500">Total</div><div className="font-bold">₹ {money(proforma.grandTotal)}</div></div><div className="bg-gray-50 p-3 rounded"><div className="text-xs text-gray-500">Outstanding</div><div className="font-bold text-red-700">₹ {money(outstandingOf(proforma))}</div></div></div>
        <div className="grid md:grid-cols-2 gap-4 mb-5"><div><div className="text-xs text-gray-500">Billing Company</div><div className="font-semibold">{proforma.companyName}</div><div className="text-sm">{proforma.companySnapshot?.pan ? `PAN: ${proforma.companySnapshot.pan}` : ''}</div><div className="text-sm">{proforma.companySnapshot?.gstin ? `GSTIN: ${proforma.companySnapshot.gstin}` : ''}</div></div><div><div className="text-xs text-gray-500">Client</div><div className="font-semibold">{proforma.clientName}</div><div className="text-sm">{addressOf(proforma.clientSnapshot)}</div></div></div>
        <table className="w-full text-sm border"><thead className="bg-gray-100"><tr><th className="p-3 text-left">Description</th><th className="p-3 text-right">Amount</th></tr></thead><tbody>{(proforma.lineItems || []).map((line) => <tr key={line.id} className="border-t"><td className="p-3">{line.description}</td><td className="p-3 text-right">₹ {money(line.amount)}</td></tr>)}</tbody><tfoot><tr className="border-t"><td className="p-2 text-right">Taxable</td><td className="p-2 text-right">₹ {money(proforma.taxableAmount)}</td></tr>{Number(proforma.gstAmount || 0) > 0 && <tr><td className="p-2 text-right">GST @ {proforma.gstRate}%</td><td className="p-2 text-right">₹ {money(proforma.gstAmount)}</td></tr>}<tr className="font-bold text-base"><td className="p-3 text-right">Grand Total</td><td className="p-3 text-right">₹ {money(proforma.grandTotal)}</td></tr></tfoot></table>
      </div>}
      {tab === 'followup' && <div className="grid md:grid-cols-2 gap-5"><div><h3 className="font-bold mb-3">Follow-up History</h3>{!(proforma.followUps || []).length && <p className="text-gray-500 text-sm">No follow-up recorded.</p>}{[...(proforma.followUps || [])].reverse().map((item) => <div key={item.id} className="border rounded p-3 mb-2"><div className="flex justify-between"><strong>{formatDate(item.date)}</strong><span className="text-xs text-gray-500">{item.addedByName}</span></div>{item.outcome && <div className="mt-1 text-xs font-medium text-slate-600">{item.outcome}</div>}<p className="text-sm mt-1">{item.remarks}</p>{item.nextFollowUpDate && <p className="text-xs text-blue-700 mt-1">Next: {formatDate(item.nextFollowUpDate)}</p>}</div>)}</div>{canManage && proforma.status !== 'Cancelled' && <form onSubmit={submitFollowUp} className="border rounded p-4"><h3 className="font-bold mb-3">Add Follow-up</h3><label className="text-sm">Date<input type="date" className="mt-1 mb-3 w-full border rounded px-3 py-2" value={followUp.date} onChange={(e) => setFollowUp({ ...followUp, date: e.target.value })} /></label><label className="text-sm">Outcome<select className="mt-1 mb-3 w-full border rounded px-3 py-2" value={followUp.outcome} onChange={(e) => setFollowUp({ ...followUp, outcome: e.target.value })}><option>Reminder sent</option><option>No response</option><option>Promised payment</option><option>Dispute</option><option>Partial payment</option><option>Paid</option></select></label><label className="text-sm">Remarks<textarea className="mt-1 mb-3 w-full border rounded px-3 py-2" rows="3" value={followUp.remarks} onChange={(e) => setFollowUp({ ...followUp, remarks: e.target.value })} /></label><label className="text-sm">Next Follow-up<input type="date" className="mt-1 mb-3 w-full border rounded px-3 py-2" value={followUp.nextFollowUpDate} onChange={(e) => setFollowUp({ ...followUp, nextFollowUpDate: e.target.value })} /></label><div className="flex flex-wrap gap-2"><button className="px-4 py-2 bg-blue-600 text-white rounded">Save Follow-up</button><button type="button" onClick={openFollowUpWhatsApp} className="px-4 py-2 bg-green-600 text-white rounded">WhatsApp Follow-up</button></div></form>}</div>}
      {tab === 'payments' && <div className="grid md:grid-cols-2 gap-5"><div><h3 className="font-bold mb-3">Payment History</h3><div className="mb-3 text-sm">Received: <strong>₹ {money(paymentTotal(proforma.payments))}</strong> · Outstanding: <strong>₹ {money(outstandingOf(proforma))}</strong></div>{!(proforma.payments || []).length && <p className="text-gray-500 text-sm">No payment recorded.</p>}{[...(proforma.payments || [])].reverse().map((item) => <div key={item.id} className="border rounded p-3 mb-2"><div className="flex justify-between"><strong>₹ {money(item.amount)}</strong><span>{formatDate(item.date)}</span></div><p className="text-sm">{item.mode}{item.reference ? ` · ${item.reference}` : ''}</p>{item.remarks && <p className="text-xs text-gray-500">{item.remarks}</p>}</div>)}</div>{canManage && proforma.status !== 'Cancelled' && outstandingOf(proforma) > 0.01 && <form onSubmit={submitPayment} className="border rounded p-4"><h3 className="font-bold mb-3">Record Payment</h3><label className="text-sm">Date<input type="date" className="mt-1 mb-3 w-full border rounded px-3 py-2" value={payment.date} onChange={(e) => setPayment({ ...payment, date: e.target.value })} /></label><label className="text-sm">Amount<input type="number" min="0.01" max={outstandingOf(proforma)} step="0.01" className="mt-1 mb-3 w-full border rounded px-3 py-2" value={payment.amount} onChange={(e) => setPayment({ ...payment, amount: e.target.value })} /></label><label className="text-sm">Mode<select className="mt-1 mb-3 w-full border rounded px-3 py-2" value={payment.mode} onChange={(e) => setPayment({ ...payment, mode: e.target.value })}><option>Bank Transfer</option><option>Cheque</option><option>Cash</option><option>UPI</option><option>Card</option><option>Other</option></select></label><label className="text-sm">Reference<input className="mt-1 mb-3 w-full border rounded px-3 py-2" value={payment.reference} onChange={(e) => setPayment({ ...payment, reference: e.target.value })} /></label><label className="text-sm">Remarks<textarea className="mt-1 mb-3 w-full border rounded px-3 py-2" value={payment.remarks} onChange={(e) => setPayment({ ...payment, remarks: e.target.value })} /></label><button className="px-4 py-2 bg-green-700 text-white rounded">Record Payment</button></form>}</div>}
    </div>
    <WhatsAppComposer
      open={Boolean(whatsappMode)}
      title={whatsappMode === 'followup' ? 'WhatsApp Payment Follow-up' : `Send ${billingDocumentLabel(proforma)} on WhatsApp`}
      contacts={contacts}
      initialMessage={whatsappInitialMessage}
      confirmLabel={whatsappMode === 'bill' ? 'Download PDF & Open WhatsApp' : 'Open WhatsApp'}
      onClose={() => setWhatsappMode('')}
      onConfirm={confirmWhatsApp}
    />
  </div></div>;
}
